import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaTimes,
  FaUsers,
  FaUserPlus,
  FaSearch,
  FaCrown,
  FaUserCog,
  FaUser,
  FaEye,
  FaTrash,
  FaEnvelope,
  FaCheck,
  FaExclamationTriangle,
  FaSort,
  FaFilter,
  FaLink,
  FaHistory,
  FaDownload,
  FaCopy,
  FaRedo,
  FaCalendarAlt,
  FaCheckSquare,
  FaSquare,
  FaChevronDown,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaClock,
  FaUserCircle,
} from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import RejectionModal from './RejectionModal';
import MemberRemovalModal from './MemberRemovalModal';
import { 
  rejectMembershipRequest, 
  removeMember, 
  approveMembershipRequest,
  getMembers,
  updateMemberRole,
  bulkApproveRequests,
  bulkRejectRequests,
  createInviteLink,
  getInviteLinks,
  revokeInviteLink,
  getActivityLog,
  exportMembers,
  searchUsers,
  inviteUsersToClub,
  getClubInvitations,
  cancelInvitation
} from '../../api/clubMembership';
import styles from './ClubManagementModal.module.css';

const ClubManagementModal = ({ club, onClose, user, initialTab = 'members' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [members, setMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userRole, setUserRole] = useState(null);
  
  // Enhanced members state
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortField, setSortField] = useState('joinedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [stats, setStats] = useState({ creators: 0, owners: 0, managers: 0, members: 0, total: 0 });
  
  // Bulk selection state
  const [selectedRequests, setSelectedRequests] = useState([]);
  
  // Invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Member');
  const [inviteLinks, setInviteLinks] = useState([]);
  const [newInviteExpiry, setNewInviteExpiry] = useState('7d');
  const [newInviteMaxUses, setNewInviteMaxUses] = useState('');
  const [showInviteLinks, setShowInviteLinks] = useState(false);
  
  // User search state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUsersToInvite, setSelectedUsersToInvite] = useState([]);
  const [inviteMessage, setInviteMessage] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showPendingInvites, setShowPendingInvites] = useState(false);
  
  // Activity log state
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  
  // Role change state
  const [editingRoleMemberId, setEditingRoleMemberId] = useState(null);
  
  // Modal states
  const [rejectionModal, setRejectionModal] = useState({
    isOpen: false,
    requestId: null,
    memberName: ''
  });
  const [removalModal, setRemovalModal] = useState({
    isOpen: false,
    memberId: null,
    memberName: ''
  });
  
  const { userProfile } = useUser();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const searchContainerRef = useRef(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce user search
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  // User search effect
  useEffect(() => {
    if (debouncedUserSearch.length >= 2 && club) {
      handleUserSearch();
    } else {
      setUserSearchResults([]);
      setShowSearchResults(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUserSearch]);

  // Click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (club && user && userProfile) {
      checkUserRole();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club, user, userProfile]);

  useEffect(() => {
    if (club && userProfile && activeTab === 'members') {
      fetchMembersEnhanced();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club, userProfile, debouncedSearch, roleFilter, sortField, sortOrder, pagination.page, activeTab]);

  useEffect(() => {
    if (club && userProfile && activeTab === 'requests') {
      fetchPendingRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club, userProfile, activeTab]);

  useEffect(() => {
    if (club && userProfile && activeTab === 'invite') {
      fetchInviteLinks();
      fetchPendingInvitations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club, userProfile, activeTab]);

  useEffect(() => {
    if (club && userProfile && activeTab === 'activity') {
      fetchActivityLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club, userProfile, activeTab]);

  const checkUserRole = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/clubs/${club._id}/membership/${userProfile._id}`);
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
    }
  };

  const fetchMembersEnhanced = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getMembers(club._id, {
        search: debouncedSearch,
        role: roleFilter,
        sort: sortField,
        order: sortOrder,
        page: pagination.page,
        limit: pagination.limit
      });
      
      setMembers(result.members || []);
      if (result.pagination) {
        setPagination(prev => ({ ...prev, ...result.pagination }));
      }
      if (result.stats) {
        setStats(result.stats);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      setError('Failed to load members');
    } finally {
      setIsLoading(false);
    }
  }, [club._id, debouncedSearch, roleFilter, sortField, sortOrder, pagination.page, pagination.limit]);

  const fetchPendingRequests = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/clubs/${club._id}/pending-requests`);
      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data);
        setSelectedRequests([]);
      }
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      setError('Failed to load pending requests');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInviteLinks = async () => {
    try {
      const result = await getInviteLinks(club._id);
      setInviteLinks(result.inviteLinks || []);
    } catch (error) {
      console.error('Error fetching invite links:', error);
    }
  };

  const fetchActivityLog = async () => {
    setActivityLoading(true);
    try {
      const result = await getActivityLog(club._id, { type: 'membership', limit: 50 });
      setActivityLog(result.activities || []);
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const result = await getClubInvitations(club._id, { status: 'pending' });
      setPendingInvitations(result.invitations || []);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
    }
  };

  // User search handler
  const handleUserSearch = async () => {
    if (debouncedUserSearch.length < 2) return;
    
    setUserSearchLoading(true);
    try {
      const result = await searchUsers({
        q: debouncedUserSearch,
        excludeClub: club._id,
        limit: 10
      });
      setUserSearchResults(result.users || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // Select user to invite
  const handleSelectUserToInvite = (user) => {
    if (!selectedUsersToInvite.find(u => u._id === user._id)) {
      setSelectedUsersToInvite(prev => [...prev, user]);
    }
    setUserSearchQuery('');
    setShowSearchResults(false);
  };

  // Remove user from invite list
  const handleRemoveUserToInvite = (userId) => {
    setSelectedUsersToInvite(prev => prev.filter(u => u._id !== userId));
  };

  // Send invitations to selected users
  const handleSendInvitations = async () => {
    if (selectedUsersToInvite.length === 0) {
      setError('Please select at least one user to invite');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const userIds = selectedUsersToInvite.map(u => u._id);
      const result = await inviteUsersToClub(club._id, userIds, inviteRole, userProfile._id, inviteMessage);
      
      const sentCount = result.results?.sent?.length || 0;
      const alreadyMemberCount = result.results?.alreadyMember?.length || 0;
      const alreadyInvitedCount = result.results?.alreadyInvited?.length || 0;
      
      let message = `${sentCount} invitation${sentCount !== 1 ? 's' : ''} sent`;
      if (alreadyMemberCount > 0) message += `, ${alreadyMemberCount} already member${alreadyMemberCount !== 1 ? 's' : ''}`;
      if (alreadyInvitedCount > 0) message += `, ${alreadyInvitedCount} already invited`;
      
      setSuccess(message);
      setSelectedUsersToInvite([]);
      setInviteMessage('');
      fetchPendingInvitations();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel a pending invitation
  const handleCancelInvitation = async (invitationId) => {
    setIsLoading(true);
    try {
      await cancelInvitation(invitationId, userProfile._id);
      setSuccess('Invitation cancelled');
      fetchPendingInvitations();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle role change
  const handleRoleChange = async (memberId, newRole) => {
    setIsLoading(true);
    setError(null);
    try {
      await updateMemberRole(club._id, memberId, newRole, userProfile._id);
      setSuccess(`Role updated to ${newRole}`);
      setEditingRoleMemberId(null);
      fetchMembersEnhanced();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk actions
  const handleSelectAll = () => {
    if (selectedRequests.length === pendingRequests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(pendingRequests.map(r => r._id));
    }
  };

  const handleSelectRequest = (requestId) => {
    setSelectedRequests(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const handleBulkApprove = async () => {
    if (selectedRequests.length === 0) return;
    setIsLoading(true);
    setError(null);
    try {
      await bulkApproveRequests(club._id, selectedRequests, userProfile._id);
      setSuccess(`${selectedRequests.length} requests approved`);
      setSelectedRequests([]);
      // Refresh all relevant lists after bulk approval
      fetchPendingRequests();
      fetchPendingInvitations();
      fetchMembersEnhanced();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedRequests.length === 0) return;
    setIsLoading(true);
    setError(null);
    try {
      await bulkRejectRequests(club._id, selectedRequests, userProfile._id);
      setSuccess(`${selectedRequests.length} requests rejected`);
      setSelectedRequests([]);
      // Refresh all relevant lists after bulk rejection
      fetchPendingRequests();
      fetchPendingInvitations();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Invite functions
  const handleInviteByEmail = async () => {
    if (!inviteEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/clubs/${club._id}/assign-role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: inviteEmail.trim(),
          role: inviteRole,
          assignedBy: userProfile._id,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to invite user');

      setSuccess(`Invitation sent successfully`);
      setInviteEmail('');
      fetchMembersEnhanced();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInviteLink = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await createInviteLink(club._id, {
        role: inviteRole,
        expiresIn: newInviteExpiry,
        maxUses: newInviteMaxUses ? parseInt(newInviteMaxUses) : null,
        createdBy: userProfile._id
      });
      setSuccess('Invite link created');
      fetchInviteLinks();
      // Copy to clipboard
      navigator.clipboard.writeText(result.inviteLink);
      setSuccess('Invite link created and copied to clipboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyInviteLink = (link) => {
    const fullUrl = `${window.location.origin}/join/${link.code}`;
    navigator.clipboard.writeText(fullUrl);
    setSuccess('Link copied to clipboard');
  };

  const handleRevokeLink = async (linkId) => {
    setIsLoading(true);
    try {
      await revokeInviteLink(club._id, linkId, userProfile._id);
      setSuccess('Invite link revoked');
      fetchInviteLinks();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Export
  const handleExport = async (format) => {
    try {
      const blob = await exportMembers(club._id, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${club.name}-members.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess(`Members exported as ${format.toUpperCase()}`);
    } catch (error) {
      setError(error.message);
    }
  };

  // Existing handlers
  const handleApproveRequest = async (requestId) => {
    setIsLoading(true);
    setError(null);
    try {
      await approveMembershipRequest(club._id, requestId, userProfile._id);
      setSuccess('Request approved');
      // Refresh all relevant lists after approval
      fetchPendingRequests();
      fetchPendingInvitations();
      fetchMembersEnhanced();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectRequest = (requestId, memberName) => {
    setRejectionModal({ isOpen: true, requestId, memberName });
  };

  const handleConfirmRejection = async (rejectionData) => {
    setIsLoading(true);
    setError(null);
    try {
      await rejectMembershipRequest(club._id, rejectionModal.requestId, userProfile._id, rejectionData);
      setSuccess('Request rejected');
      setRejectionModal({ isOpen: false, requestId: null, memberName: '' });
      // Refresh all relevant lists after rejection
      fetchPendingRequests();
      fetchPendingInvitations();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = (memberId, memberName) => {
    setRemovalModal({ isOpen: true, memberId, memberName });
  };

  const handleConfirmRemoval = async (removalData) => {
    setIsLoading(true);
    setError(null);
    try {
      await removeMember(club._id, removalModal.memberId, userProfile._id, removalData);
      setSuccess('Member removed');
      setRemovalModal({ isOpen: false, memberId: null, memberName: '' });
      fetchMembersEnhanced();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions
  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'owner':
      case 'creator':
        return <FaCrown className={styles.roleIcon} />;
      case 'manager':
        return <FaUserCog className={styles.roleIcon} />;
      case 'member':
        return <FaUser className={styles.roleIcon} />;
      default:
        return <FaEye className={styles.roleIcon} />;
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'owner':
      case 'creator':
        return styles.roleOwner;
      case 'manager':
        return styles.roleManager;
      case 'member':
        return styles.roleMember;
      default:
        return styles.roleViewer;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return formatDate(dateString);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'member_approved':
        return <FaCheck className={styles.activityIconApprove} />;
      case 'member_rejected':
        return <FaTimes className={styles.activityIconReject} />;
      case 'role_changed':
        return <FaUserCog className={styles.activityIconRole} />;
      case 'invite_created':
        return <FaLink className={styles.activityIconInvite} />;
      case 'invite_used':
        return <FaUserPlus className={styles.activityIconJoin} />;
      default:
        return <FaHistory className={styles.activityIconDefault} />;
    }
  };

  const canAssignRole = (targetRole) => {
    const currentRole = userRole?.toLowerCase();
    const target = targetRole?.toLowerCase();
    
    if (currentRole === 'creator') return true;
    if (currentRole === 'owner' && target !== 'creator' && target !== 'owner') return true;
    if (currentRole === 'manager' && target === 'member') return true;
    return false;
  };

  const canManageRoles = ['creator', 'owner', 'manager'].includes(userRole?.toLowerCase());

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  if (!canManageRoles) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <FaUsers className={styles.modalTitleIcon} />
              Club Management
            </div>
            <button className={styles.closeButton} onClick={onClose}>
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.accessDenied}>
              <FaExclamationTriangle className={styles.accessDeniedIcon} />
              <h3>Access Denied</h3>
              <p>Only Creators, Owners, and Managers can access club management.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!club || !club._id || !userProfile || !userProfile._id) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FaUsers className={styles.modalTitleIcon} />
            Manage {club.name}
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabContainer}>
          <button
            className={`${styles.tabButton} ${activeTab === 'members' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('members')}
          >
            <FaUsers className={styles.tabIcon} />
            Members ({stats.total || members.length})
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'requests' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            <FaEnvelope className={styles.tabIcon} />
            Requests ({pendingRequests.length})
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'invite' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('invite')}
          >
            <FaUserPlus className={styles.tabIcon} />
            Invite
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'activity' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <FaHistory className={styles.tabIcon} />
            Activity
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Messages */}
          {success && (
            <div className={styles.successMessage}>
              <FaCheck className={styles.successIcon} />
              <p>{success}</p>
            </div>
          )}
          {error && (
            <div className={styles.errorMessage}>
              <FaExclamationTriangle className={styles.errorIcon} />
              <p>{error}</p>
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className={styles.tabContent}>
              {/* Stats Bar */}
              <div className={styles.statsBar}>
                <span className={styles.statItem}>
                  <FaCrown className={styles.statIconOwner} /> {stats.creators + stats.owners} Owners
                </span>
                <span className={styles.statItem}>
                  <FaUserCog className={styles.statIconManager} /> {stats.managers} Managers
                </span>
                <span className={styles.statItem}>
                  <FaUser className={styles.statIconMember} /> {stats.members} Members
                </span>
                <button className={styles.exportButton} onClick={() => handleExport('csv')} title="Export CSV">
                  <FaDownload /> Export
                </button>
              </div>

              {/* Search & Filter Bar */}
              <div className={styles.filterBar}>
                <div className={styles.searchInput}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                  />
                </div>
                <div className={styles.filterGroup}>
                  <FaFilter className={styles.filterIcon} />
                  <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                    <option value="">All Roles</option>
                    <option value="Creator">Creator</option>
                    <option value="Owner">Owner</option>
                    <option value="Manager">Manager</option>
                    <option value="Member">Member</option>
                  </select>
                </div>
                <div className={styles.sortGroup}>
                  <FaSort className={styles.sortIcon} />
                  <select value={sortField} onChange={(e) => setSortField(e.target.value)}>
                    <option value="joinedAt">Join Date</option>
                    <option value="name">Name</option>
                    <option value="role">Role</option>
                  </select>
                  <button 
                    className={styles.sortOrderButton}
                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>

              {/* Members List */}
              {isLoading ? (
                <div className={styles.loading}>Loading members...</div>
              ) : members.length > 0 ? (
                <>
                  <div className={styles.membersList}>
                    {members.map((member) => (
                      <div key={member._id} className={styles.memberItem}>
                        <div className={styles.memberInfo}>
                          <div className={styles.memberAvatar}>
                            {member.user?.profileImage?.url || member.user?.profileImage ? (
                              <img 
                                src={member.user?.profileImage?.url || member.user?.profileImage} 
                                alt={member.user?.firstName || 'Member'} 
                              />
                            ) : (
                              <FaUser />
                            )}
                          </div>
                          <div className={styles.memberDetails}>
                            <h4>
                              {member.user
                                ? `${[member.user.firstName, member.user.lastName].filter(Boolean).join(' ')}`.trim() || member.user.email
                                : 'Unknown'}
                            </h4>
                            <p>{member.user?.email || ''}</p>
                            <span className={styles.joinDate}>
                              <FaCalendarAlt /> Joined {formatRelativeTime(member.joinedAt)}
                            </span>
                          </div>
                        </div>
                        <div className={styles.memberActions}>
                          {editingRoleMemberId === member._id ? (
                            <div className={styles.roleEditDropdown}>
                              <select 
                                value={member.role}
                                onChange={(e) => handleRoleChange(member._id, e.target.value)}
                                autoFocus
                                onBlur={() => setEditingRoleMemberId(null)}
                              >
                                {canAssignRole('member') && <option value="Member">Member</option>}
                                {canAssignRole('manager') && <option value="Manager">Manager</option>}
                                {canAssignRole('owner') && <option value="Owner">Owner</option>}
                              </select>
                            </div>
                          ) : (
                            <button 
                              className={`${styles.roleBadge} ${getRoleColor(member.role)}`}
                              onClick={() => member.role?.toLowerCase() !== 'creator' && setEditingRoleMemberId(member._id)}
                              disabled={member.role?.toLowerCase() === 'creator'}
                              title={member.role?.toLowerCase() === 'creator' ? 'Creator role cannot be changed' : 'Click to change role'}
                            >
                              {getRoleIcon(member.role)}
                              {member.role}
                              {member.role?.toLowerCase() !== 'creator' && <FaChevronDown className={styles.dropdownArrow} />}
                            </button>
                          )}
                          {member.user?._id !== userProfile._id && member.role?.toLowerCase() !== 'creator' && (
                            <button
                              className={styles.removeButton}
                              onClick={() => handleRemoveMember(
                                member.user?._id || member._id,
                                `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || member.user?.email || 'Member'
                              )}
                              title="Remove member"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button 
                        disabled={pagination.page <= 1}
                        onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      >
                        Previous
                      </button>
                      <span>Page {pagination.page} of {pagination.totalPages}</span>
                      <button 
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.emptyState}>
                  <FaUsers className={styles.emptyIcon} />
                  <p>No members found</p>
                </div>
              )}
            </div>
          )}

          {/* Pending Requests Tab */}
          {activeTab === 'requests' && (
            <div className={styles.tabContent}>
              {pendingRequests.length > 0 && (
                <div className={styles.bulkActions}>
                  <button 
                    className={styles.selectAllButton}
                    onClick={handleSelectAll}
                  >
                    {selectedRequests.length === pendingRequests.length ? <FaCheckSquare /> : <FaSquare />}
                    Select All ({pendingRequests.length})
                  </button>
                  {selectedRequests.length > 0 && (
                    <>
                      <button 
                        className={styles.bulkApproveButton}
                        onClick={handleBulkApprove}
                        disabled={isLoading}
                      >
                        <FaCheck /> Approve ({selectedRequests.length})
                      </button>
                      <button 
                        className={styles.bulkRejectButton}
                        onClick={handleBulkReject}
                        disabled={isLoading}
                      >
                        <FaTimes /> Reject ({selectedRequests.length})
                      </button>
                    </>
                  )}
                </div>
              )}

              {isLoading ? (
                <div className={styles.loading}>Loading requests...</div>
              ) : pendingRequests.length > 0 ? (
                <div className={styles.requestsList}>
                  {pendingRequests.map((request) => (
                    <div key={request._id} className={styles.requestItem}>
                      <div className={styles.requestCheckbox}>
                        <button onClick={() => handleSelectRequest(request._id)}>
                          {selectedRequests.includes(request._id) ? <FaCheckSquare /> : <FaSquare />}
                        </button>
                      </div>
                      <div className={styles.requestInfo}>
                        <div className={styles.memberAvatar}>
                          {request.user?.profileImage?.url || request.user?.profileImage ? (
                            <img 
                              src={request.user?.profileImage?.url || request.user?.profileImage} 
                              alt={request.user?.firstName || 'User'} 
                            />
                          ) : (
                            <FaUser />
                          )}
                        </div>
                        <div className={styles.memberDetails}>
                          <h4>
                            {request.user?.firstName && request.user?.lastName 
                              ? `${request.user.firstName} ${request.user.lastName}`.trim()
                              : request.userName || request.userEmail || 'Unknown User'
                            }
                          </h4>
                          <p>{request.user?.email || request.userEmail || 'No email'}</p>
                          <div className={styles.requestMeta}>
                            <span className={styles.requestType}>
                              {request.membershipType === 'paid' ? '💳 Paid' : '🆓 Free'}
                            </span>
                            {request.requestedAt && (
                              <span className={styles.requestDate}>
                                Requested {formatRelativeTime(request.requestedAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className={styles.requestActions}>
                        <button
                          className={styles.approveButton}
                          onClick={() => handleApproveRequest(request._id)}
                          disabled={isLoading}
                        >
                          <FaCheck /> Approve
                        </button>
                        <button
                          className={styles.rejectButton}
                          onClick={() => handleRejectRequest(
                            request._id,
                            `${request.user?.firstName || ''} ${request.user?.lastName || ''}`.trim() || request.userEmail || 'User'
                          )}
                          disabled={isLoading}
                        >
                          <FaTimes /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <FaEnvelope className={styles.emptyIcon} />
                  <p>No pending requests</p>
                </div>
              )}
            </div>
          )}

          {/* Invite Tab */}
          {activeTab === 'invite' && (
            <div className={styles.tabContent}>
              {/* User Search Section */}
              <div className={styles.inviteSection}>
                <h3><FaSearch /> Find & Invite Users</h3>
                <p className={styles.sectionDescription}>
                  Search for existing Sport Loop users to invite to your club
                </p>
                
                <div className={styles.userSearchContainer} ref={searchContainerRef}>
                  <div className={styles.searchInputWrapper}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      onFocus={() => userSearchResults.length > 0 && setShowSearchResults(true)}
                      placeholder="Search users by name or email..."
                      className={styles.userSearchInput}
                    />
                    {userSearchLoading && <span className={styles.searchSpinner}>⏳</span>}
                  </div>

                  {/* Search Results Dropdown */}
                  {showSearchResults && userSearchResults.length > 0 && (
                    <div className={styles.searchResultsDropdown}>
                      {userSearchResults.map((searchUser) => (
                        <div 
                          key={searchUser._id} 
                          className={styles.searchResultItem}
                          onClick={() => handleSelectUserToInvite(searchUser)}
                        >
                          <div className={styles.searchResultAvatar}>
                            {searchUser.profileImage?.url ? (
                              <img src={searchUser.profileImage.url} alt={searchUser.firstName} />
                            ) : (
                              <FaUserCircle />
                            )}
                          </div>
                          <div className={styles.searchResultInfo}>
                            <span className={styles.searchResultName}>
                              {searchUser.firstName} {searchUser.lastName}
                            </span>
                            <span className={styles.searchResultEmail}>{searchUser.email}</span>
                            {searchUser.location?.city && (
                              <span className={styles.searchResultLocation}>
                                <FaMapMarkerAlt /> {searchUser.location.city}
                                {searchUser.location.state && `, ${searchUser.location.state}`}
                              </span>
                            )}
                          </div>
                          <button className={styles.addUserButton}>
                            <FaUserPlus /> Add
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No results message */}
                  {showSearchResults && userSearchQuery.length >= 2 && userSearchResults.length === 0 && !userSearchLoading && (
                    <div className={styles.noSearchResults}>
                      <FaUser className={styles.noResultsIcon} />
                      <p>No users found matching "{userSearchQuery}"</p>
                      <span>Try a different search or invite by email below</span>
                    </div>
                  )}
                </div>

                {/* Selected Users to Invite */}
                {selectedUsersToInvite.length > 0 && (
                  <div className={styles.selectedUsersSection}>
                    <h4>Selected Users ({selectedUsersToInvite.length})</h4>
                    <div className={styles.selectedUsersList}>
                      {selectedUsersToInvite.map((selectedUser) => (
                        <div key={selectedUser._id} className={styles.selectedUserChip}>
                          <div className={styles.chipAvatar}>
                            {selectedUser.profileImage?.url ? (
                              <img src={selectedUser.profileImage.url} alt={selectedUser.firstName} />
                            ) : (
                              <FaUserCircle />
                            )}
                          </div>
                          <span>{selectedUser.firstName} {selectedUser.lastName}</span>
                          <button 
                            className={styles.removeChipButton}
                            onClick={() => handleRemoveUserToInvite(selectedUser._id)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <div className={styles.inviteOptionsRow}>
                      <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                        <option value="Member">Invite as Member</option>
                        <option value="Manager">Invite as Manager</option>
                        {userRole?.toLowerCase() === 'creator' && <option value="Owner">Invite as Owner</option>}
                      </select>
                      <input
                        type="text"
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                        placeholder="Add a personal message (optional)"
                        className={styles.inviteMessageInput}
                      />
                    </div>
                    
                    <button 
                      className={styles.sendInvitationsButton}
                      onClick={handleSendInvitations}
                      disabled={isLoading}
                    >
                      <FaPaperPlane /> {isLoading ? 'Sending...' : `Send ${selectedUsersToInvite.length} Invitation${selectedUsersToInvite.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.divider}>
                <span>or invite by email</span>
              </div>

              {/* Email Invite Section */}
              <div className={styles.inviteSection}>
                <h3><FaEnvelope /> Invite by Email</h3>
                <div className={styles.inviteForm}>
                  <div className={styles.formRow}>
                    <div className={styles.searchInput}>
                      <FaEnvelope className={styles.searchIcon} />
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="Enter email address"
                      />
                    </div>
                    <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                      <option value="Member">Member</option>
                      <option value="Manager">Manager</option>
                      {userRole?.toLowerCase() === 'creator' && <option value="Owner">Owner</option>}
                    </select>
                    <button 
                      className={styles.inviteButton}
                      onClick={handleInviteByEmail}
                      disabled={isLoading || !inviteEmail.trim()}
                    >
                      {isLoading ? 'Sending...' : 'Send Invite'}
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.divider}>
                <span>or share a link</span>
              </div>

              {/* Invite Link Section */}
              <div className={styles.inviteSection}>
                <h3><FaLink /> Create Invite Link</h3>
                <div className={styles.inviteLinkForm}>
                  <div className={styles.formRow}>
                    <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                      <option value="Member">Join as Member</option>
                      <option value="Manager">Join as Manager</option>
                    </select>
                    <select value={newInviteExpiry} onChange={(e) => setNewInviteExpiry(e.target.value)}>
                      <option value="1d">Expires in 1 day</option>
                      <option value="7d">Expires in 7 days</option>
                      <option value="30d">Expires in 30 days</option>
                      <option value="never">Never expires</option>
                    </select>
                    <input
                      type="number"
                      value={newInviteMaxUses}
                      onChange={(e) => setNewInviteMaxUses(e.target.value)}
                      placeholder="Max uses (optional)"
                      min="1"
                    />
                    <button 
                      className={styles.createLinkButton}
                      onClick={handleCreateInviteLink}
                      disabled={isLoading}
                    >
                      <FaLink /> Create Link
                    </button>
                  </div>
                </div>

                {/* Existing Links */}
                <div className={styles.existingLinks}>
                  <button 
                    className={styles.toggleLinksButton}
                    onClick={() => setShowInviteLinks(!showInviteLinks)}
                  >
                    {showInviteLinks ? 'Hide' : 'Show'} Existing Links ({inviteLinks.length})
                  </button>
                  
                  {showInviteLinks && inviteLinks.length > 0 && (
                    <div className={styles.linksList}>
                      {inviteLinks.map((link) => (
                        <div key={link._id} className={`${styles.linkItem} ${!link.isActive ? styles.linkInactive : ''}`}>
                          <div className={styles.linkInfo}>
                            <code>{link.code}</code>
                            <span className={styles.linkMeta}>
                              {link.role} • {link.currentUses}/{link.maxUses || '∞'} uses
                              {link.expiresAt && ` • Expires ${formatDate(link.expiresAt)}`}
                            </span>
                          </div>
                          <div className={styles.linkActions}>
                            <button onClick={() => handleCopyInviteLink(link)} title="Copy link">
                              <FaCopy />
                            </button>
                            {link.isActive && (
                              <button onClick={() => handleRevokeLink(link._id)} title="Revoke link" className={styles.revokeButton}>
                                <FaTimes />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Pending Invitations */}
              {pendingInvitations.length > 0 && (
                <div className={styles.inviteSection}>
                  <button 
                    className={styles.toggleLinksButton}
                    onClick={() => setShowPendingInvites(!showPendingInvites)}
                  >
                    <FaClock /> {showPendingInvites ? 'Hide' : 'Show'} Pending Invitations ({pendingInvitations.length})
                  </button>
                  
                  {showPendingInvites && (
                    <div className={styles.pendingInvitesList}>
                      {pendingInvitations.map((invitation) => (
                        <div key={invitation._id} className={styles.pendingInviteItem}>
                          <div className={styles.pendingInviteInfo}>
                            <div className={styles.memberAvatar}>
                              {invitation.invitedUser?.profileImage?.url ? (
                                <img src={invitation.invitedUser.profileImage.url} alt="" />
                              ) : (
                                <FaUserCircle />
                              )}
                            </div>
                            <div className={styles.pendingInviteDetails}>
                              <span className={styles.pendingInviteName}>
                                {invitation.invitedUser?.firstName} {invitation.invitedUser?.lastName}
                              </span>
                              <span className={styles.pendingInviteEmail}>
                                {invitation.invitedUser?.email}
                              </span>
                              <span className={styles.pendingInviteMeta}>
                                <FaClock /> Invited {formatRelativeTime(invitation.createdAt)} as {invitation.role}
                              </span>
                            </div>
                          </div>
                          <button 
                            className={styles.cancelInviteButton}
                            onClick={() => handleCancelInvitation(invitation._id)}
                            title="Cancel invitation"
                          >
                            <FaTimes /> Cancel
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div className={styles.tabContent}>
              <div className={styles.activityHeader}>
                <h3>Recent Activity</h3>
                <button 
                  className={styles.refreshButton}
                  onClick={fetchActivityLog}
                  disabled={activityLoading}
                >
                  <FaRedo className={activityLoading ? styles.spinning : ''} />
                </button>
              </div>

              {activityLoading ? (
                <div className={styles.loading}>Loading activity...</div>
              ) : activityLog.length > 0 ? (
                <div className={styles.activityList}>
                  {activityLog.map((activity) => (
                    <div key={activity._id} className={styles.activityItem}>
                      <div className={styles.activityIcon}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className={styles.activityContent}>
                        <p>
                          <strong>{activity.actor?.firstName} {activity.actor?.lastName}</strong>
                          {' '}
                          {activity.type === 'member_approved' && 'approved'}
                          {activity.type === 'member_rejected' && 'rejected'}
                          {activity.type === 'role_changed' && 'changed role for'}
                          {activity.type === 'invite_created' && 'created invite link'}
                          {activity.type === 'invite_used' && 'joined via invite'}
                          {' '}
                          {activity.target && (
                            <strong>{activity.target.firstName} {activity.target.lastName}</strong>
                          )}
                          {activity.type === 'role_changed' && activity.details && (
                            <span className={styles.roleChange}>
                              {' '}({activity.details.fromRole} → {activity.details.toRole})
                            </span>
                          )}
                        </p>
                        <span className={styles.activityTime}>
                          {formatRelativeTime(activity.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <FaHistory className={styles.emptyIcon} />
                  <p>No activity yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      <RejectionModal
        isOpen={rejectionModal.isOpen}
        onClose={() => setRejectionModal({ isOpen: false, requestId: null, memberName: '' })}
        onConfirm={handleConfirmRejection}
        memberName={rejectionModal.memberName}
        isLoading={isLoading}
      />

      {/* Member Removal Modal */}
      <MemberRemovalModal
        isOpen={removalModal.isOpen}
        onClose={() => setRemovalModal({ isOpen: false, memberId: null, memberName: '' })}
        onConfirm={handleConfirmRemoval}
        memberName={removalModal.memberName}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ClubManagementModal;
